import React from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import type { IStation } from '../../../interfaces/Station/IStation.ts';
import {
  overviewItemTitleStyles,
  overviewItemValueStyles,
  overviewSectionContainerStyles,
  overviewSectionTitleStyles,
} from '../../../styles/ts/overviewStyles.ts';
import AleaPhoneIcon from '../../../icons/AleaPhoneIcon.tsx';

type LivingAreasSectionProps = {
  isArchived: boolean;
  canEdit: boolean;
  stations: IStation[];
  isStationsLoading: boolean;
  activeActionRowId: string | null;
  onSetActiveActionRowId: (id: string | null) => void;
  onCreateNewLivingArea: () => void;
  onEditLivingArea: (station: IStation) => void;
  onDeleteLivingArea: (station: IStation) => void;
};

const LivingAreasSection: React.FC<LivingAreasSectionProps> = ({
  isArchived,
  canEdit,
  stations,
  isStationsLoading,
  activeActionRowId,
  onSetActiveActionRowId,
  onCreateNewLivingArea,
  onEditLivingArea,
  onDeleteLivingArea,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{ ...overviewSectionContainerStyles, pb: '50px', mb: '50px' }}
      w="100%"
    >
      <Box opacity={isArchived ? 0.5 : 1}>
        {/*section title*/}
        <Flex
          alignItems={{ base: 'start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          mb="20px"
          justifyContent={{ base: 'flex-start', md: 'space-between' }}
        >
          <Box>
            <Text sx={overviewSectionTitleStyles} mb={{ base: 1, md: 1 }}>
              {t('livingAreas')}
            </Text>
            <Text
              fontSize="15px"
              color="alea.500"
              mb={{ base: '10px', md: 0 }}
            >
              {t('nameLivingAreasOfCareHome')}
            </Text>
          </Box>
          {canEdit && (
            <Button
              data-testid="living-areas-new-button"
              leftIcon={<AddIcon />}
              colorScheme="teal"
              variant="solid"
              h="50px"
              borderRadius="20px"
              fontWeight={400}
              fontSize="20px"
              onClick={onCreateNewLivingArea}
              minW="250px"
            >
              {t('newLivingArea')}
            </Button>
          )}
        </Flex>

        {/* simple table */}
        <Box
          borderRadius="20px"
          overflow="hidden"
          border="1px solid #EAEAEA"
          maxW="675px"
          w="100%"
          mx="auto"
        >
          {/* header */}
          <Flex
            bg="#F3F2F2"
            px="16px"
            py="14px"
            align="center"
            borderBottom="1px solid #EAEAEA"
          >
            <Box flex="1">
              <Text sx={overviewItemTitleStyles}>
                {t('general.wohnbereichsname')}
              </Text>
            </Box>
            <Box flex="1">
              <Text sx={overviewItemTitleStyles}>{t('phone')}</Text>
            </Box>
            {canEdit && <Box w="25px" />}
          </Flex>

          {/* rows */}
          {stations.length > 0 ? (
            stations.map((station, index) => {
              const tel =
                station?.compound_phone_number &&
                `${station.compound_phone_number.code} ${station.compound_phone_number.phone}`;
              const isActive = activeActionRowId === station.id;
              const isStriped = index % 2 === 1;
              const rowBg = isActive
                ? '#F8F8F8'
                : isStriped
                  ? '#F3F2F2'
                  : 'white';

              return (
                <Flex
                  key={station.id}
                  bg={rowBg}
                  align="center"
                  h="50px"
                  borderBottom="1px solid #EAEAEA"
                  position={isActive ? 'relative' : 'static'}
                  zIndex={isActive ? 1501 : 'auto'}
                  boxShadow={
                    isActive ? '0px 8px 24px rgba(0,0,0,0.12)' : 'none'
                  }
                  _last={{ borderBottom: 'none' }}
                >
                  <Box flex="1" px="16px">
                    <Text sx={overviewItemValueStyles}>{station.name}</Text>
                  </Box>
                  <Box
                    flex="1"
                    display="flex"
                    alignItems="center"
                    px="16px"
                  >
                    {tel ? (
                      <Flex align="center">
                        <Box mr="8px">
                          <AleaPhoneIcon size="20" />
                        </Box>
                        <Text
                          sx={overviewItemValueStyles}
                          textDecoration="underline"
                        >
                          {tel}
                        </Text>
                      </Flex>
                    ) : (
                      <Text sx={overviewItemValueStyles}>-</Text>
                    )}
                  </Box>

                  {canEdit && (
                    <Menu
                      isOpen={activeActionRowId === station.id}
                      onOpen={() => onSetActiveActionRowId(station.id)}
                      onClose={() => onSetActiveActionRowId(null)}
                      placement="bottom-end"
                      autoSelect={false}
                    >
                      <MenuButton
                        as={Box}
                        w="25px"
                        h="50px"
                        mt="1px"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        bg={isActive ? 'alea.500' : '#F3F2F2'}
                        borderLeft="1px solid #EAEAEA"
                        cursor="pointer"
                      >
                        <IconButton
                          aria-label="more"
                          variant="ghost"
                          color={isActive ? 'white' : '#6E6E6E'}
                          size="xs"
                          w="25px"
                          h="50px"
                          cursor="pointer"
                          _hover={{ background: 'transparent' }}
                          icon={
                            <Box as="span" fontSize="20px">
                              ⋮
                            </Box>
                          }
                        />
                      </MenuButton>
                      <Portal>
                        <MenuList
                          borderRadius="20px"
                          border="1px solid #DCDCDC"
                          boxShadow="0px 8px 24px rgba(0,0,0,0.15)"
                          p="0"
                          zIndex={1502}
                          overflow="hidden"
                          bg="white"
                          motionProps={{
                            variants: {
                              enter: { opacity: 1, scale: 1 },
                              exit: {
                                opacity: 0,
                                scale: 1,
                              },
                            },
                          }}
                        >
                          <MenuItem
                            onClick={() => onEditLivingArea(station)}
                            fontSize="18px"
                            h="52px"
                            borderBottom="1px solid #DCDCDC"
                          >
                            {t('edit')}
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              onSetActiveActionRowId(null);
                              onDeleteLivingArea(station);
                            }}
                            fontSize="18px"
                            h="52px"
                            color="#F04438"
                          >
                            {t('delete')}
                          </MenuItem>
                        </MenuList>
                      </Portal>
                    </Menu>
                  )}
                </Flex>
              );
            })
          ) : (
            <Flex
              bg="white"
              px="16px"
              py="12px"
              align="center"
              textAlign="center"
              justifyContent="center"
            >
              <Text sx={overviewItemValueStyles}>
                {isStationsLoading
                  ? t('general.loading', { defaultValue: 'Loading...' })
                  : t('noDataYet')}
              </Text>
            </Flex>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LivingAreasSection;

